import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "@sequentech/ui-essentials";
import { SlateDialog } from "../SlateDialog/SlateDialog";
import { translate } from "@sequentech/ui-core";
import { useTranslation } from "react-i18next";
import { sortCandidatesInBallotOrder, sortContestsInBallotOrder } from "../../services/ContestOrder";
var ChangeRow = styled("div")(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            "padding": "15px 0",
            "borderBottom": "1px solid #e7eaee",
            "overflowWrap": "anywhere",
            "&:last-of-type": {
                borderBottom: "none",
            },
            "& .slate-change-columns": {
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) 24px minmax(0, 1fr)",
                gap: "8px",
                fontSize: "14px",
                lineHeight: 1.8,
            },
            "& .slate-change-label": {
                display: "block",
                marginBottom: "5px",
                fontSize: "12px",
                lineHeight: 1.4,
                color: theme.palette.customGrey.dark,
            },
            "& .slate-change-arrow": {
                paddingTop: "26px",
                color: theme.palette.customGrey.dark,
            },
            "& .slate-change-after": {
                fontWeight: 500,
                color: theme.palette.brandColor,
            }
        },
        _b[theme.breakpoints.down("sm")] = {
            "& .slate-change-columns": {
                gridTemplateColumns: "minmax(0, 1fr) 15px minmax(0, 1fr)",
                gap: "7px",
                fontSize: "13px",
            },
            "& .slate-change-label": {
                fontSize: "11px",
            },
        },
        _b);
});
var Notice = styled("div")(function (_a) {
    var theme = _a.theme;
    return ({
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        marginTop: "18px",
        padding: "13px 15px",
        border: "1px solid #e7eaee",
        borderRadius: "7px",
        background: theme.palette.lightBackground,
        color: theme.palette.customGrey.dark,
        fontSize: "14px",
        lineHeight: 1.45,
    });
});
/**
 * The before and after of every contest that loses a choice. A contest left
 * without any choice says so instead of naming a candidate.
 */
export var SlateChangesDialog = function (_a) {
    var className = _a.className, pending = _a.pending, current = _a.current, contests = _a.contests, title = _a.title, content = _a.content, afterLabel = _a.afterLabel, note = _a.note, ok = _a.ok, cancel = _a.cancel, handleClose = _a.handleClose;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var namesOf = function (contest, selection) {
        var entry = selection === null || selection === void 0 ? void 0 : selection.find(function (item) { return item.contest_id === contest.id; });
        return sortCandidatesInBallotOrder(contest, contest.candidates)
            .filter(function (candidate) {
            return entry === null || entry === void 0 ? void 0 : entry.choices.some(function (choice) { return choice.id === candidate.id && choice.selected > -1; });
        })
            .map(function (candidate) { var _a; return (_a = translate(candidate, "name", i18n.language)) !== null && _a !== void 0 ? _a : candidate.id; });
    };
    var lines = function (names) {
        return names.map(function (name, index) { return (_jsx("span", { className: "slate-change-candidate", style: { display: "block" }, children: name }, index)); });
    };
    return (_jsx(SlateDialog, { className: className, open: !!pending, handleClose: handleClose, title: title, ok: ok, cancel: cancel, children: _jsxs(Box, { className: "slate-changes", sx: { overflowWrap: "anywhere" }, children: [_jsx(Typography, { className: "slate-replace-content", sx: {
                        margin: "0 0 12px",
                        fontSize: "inherit",
                        lineHeight: "inherit",
                        color: "customGrey.dark",
                    }, children: content }), sortContestsInBallotOrder(contests).map(function (contest) {
                    var _a;
                    var change = pending === null || pending === void 0 ? void 0 : pending.changes.find(function (entry) { return entry.contestId === contest.id; });
                    if (!change || change.removed.length === 0) {
                        return null;
                    }
                    var after = namesOf(contest, pending === null || pending === void 0 ? void 0 : pending.selection);
                    return (_jsxs(ChangeRow, { className: "slate-replace-contest", children: [_jsx(Typography, { className: "slate-replace-contest-name", component: "h3", sx: { fontSize: "15px", fontWeight: 700, margin: "0 0 11px" }, children: (_a = translate(contest, "name", i18n.language)) !== null && _a !== void 0 ? _a : contest.id }), _jsxs("div", { className: "slate-change-columns", children: [_jsxs("div", { className: "slate-replace-removed", children: [_jsx("span", { className: "slate-change-label", children: t("slates.apply.replaceDialog.current") }), lines(namesOf(contest, current))] }), _jsx("div", { className: "slate-change-arrow", "aria-hidden": "true", children: "\u2192" }), _jsxs("div", { className: "slate-replace-added slate-change-after".concat(after.length ? "" : " slate-replace-none"), children: [_jsx("span", { className: "slate-change-label", children: afterLabel }), after.length ? lines(after) : lines([t("slates.noCandidate")])] })] })] }, contest.id));
                }), note ? (_jsxs(Notice, { className: "slate-replace-note", children: [_jsx(Icon, { className: "slate-replace-note-icon", icon: faCircleInfo, "aria-hidden": "true", style: { marginTop: "3px" } }), _jsx("span", { className: "slate-replace-note-text", children: note })] })) : null] }) }));
};
